import { HttpBackend, HttpHeaders, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';

/**
 * Renovação de access token via POST /api/auth/refresh (sem passar pelos interceptors HTTP).
 * Uma única renovação em voo para todos os 401 concorrentes (rotação do refresh no AuthService).
 */
@Injectable({ providedIn: 'root' })
export class TokenRefreshService {
  private readonly auth = inject(AuthService);
  private readonly httpBackend = inject(HttpBackend);

  private inFlight: Promise<boolean> | null = null;

  refreshIfPossible(): Promise<boolean> {
    if (this.inFlight) return this.inFlight;
    const refreshToken = this.auth.getRefreshToken();
    if (!refreshToken) return Promise.resolve(false);

    const promise = this.performRefresh(refreshToken);
    this.inFlight = promise;
    void promise.finally(() => {
      if (this.inFlight === promise) this.inFlight = null;
    });
    return promise;
  }

  private async performRefresh(refreshToken: string): Promise<boolean> {
    const base = environment.apiUrl.replace(/\/$/, '');
    const refreshUrl = `${base}/api/auth/refresh`;
    try {
      const req = new HttpRequest('POST', refreshUrl, { refreshToken }, {
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
      });
      const body = await firstValueFrom(
        this.httpBackend.handle(req).pipe(
          filter((event): event is HttpResponse<unknown> => event instanceof HttpResponse),
          take(1),
          map((event) => event.body)
        )
      );
      return this.auth.applyRefreshResponse(body);
    } catch (error) {
      if (!environment.production) {
        console.warn('[Taskeo API] refresh token falhou', refreshUrl, error);
      }
      return false;
    }
  }
}
